/**
 * Calculate Stio's stardate paradigm
 * Format: [century][year][month].[day][percent-of-day]
 * Example: February 22, 2026 at 14:30 → 12602.2260
 */
export function calculateStioStardate(date: Date): string {
  const year = date.getFullYear();
  const month = date.getMonth() + 1; // 0-indexed
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = date.getSeconds();
  
  // Calculate percent of day completed
  const totalSecondsInDay = 24 * 60 * 60;
  const secondsElapsed = (hours * 60 * 60) + (minutes * 60) + seconds;
  const percentOfDay = Math.floor((secondsElapsed / totalSecondsInDay) * 100);
  
  // Extract century after 20th (21st century = 1, 22nd = 2, etc.)
  // For 2000-2099: floor(2026/100) = 20, so 20-19 = 1
  // For 2100-2199: floor(2126/100) = 21, so 21-19 = 2
  const centuryAfter20th = Math.floor(year / 100) - 19;
  const yearInCentury = year % 100;
  
  // Format: [century][year][month].[day][percent]
  const integerPart = `${centuryAfter20th}${yearInCentury.toString().padStart(2, '0')}${month.toString().padStart(2, '0')}`;
  const decimalPart = `${day.toString().padStart(2, '0')}${percentOfDay.toString().padStart(2, '0')}`;
  
  return `${integerPart}.${decimalPart}`;
}

/**
 * Calculate STA canonical stardate (23rd century era)
 * Formula: 2000 + ((current_year - 2024) * 1000) + (day_of_year * 2.7397)
 * Example: February 22, 2026 → 4147.9
 */
export function calculateSTAStardate(date: Date): string {
  const year = date.getFullYear();
  const startOfYear = new Date(year, 0, 1);
  const diff = date.getTime() - startOfYear.getTime();
  const oneDay = 1000 * 60 * 60 * 24;
  const dayOfYear = Math.floor(diff / oneDay) + 1;
  
  // Include fractional day based on time
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const fractionOfDay = (hours * 60 + minutes) / (24 * 60);
  const dayWithFraction = dayOfYear + fractionOfDay;
  
  // STA formula
  const stardate = 2000 + ((year - 2024) * 1000) + (dayWithFraction * 2.7397);
  
  return stardate.toFixed(1);
}

/**
 * Get both stardates formatted for display
 */
export function getStardates(date: Date = new Date()): { stio: string; sta: string } {
  return {
    stio: calculateStioStardate(date),
    sta: calculateSTAStardate(date),
  };
}
